import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
const RECAPTCHA_SITE_KEY = '6Lfz5QwsAAAAAOAkHId4fBaVB01l-uovzxvltPs2';
const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    cl_name: '',
    contact_email: '',
    password: '',
    confirmPassword: '',
    id_client_type: '',
    product_id: '',
    email_honeypot: ''
  });
  const [clientTypes, setClientTypes] = useState([]);
  const [products, setProducts] = useState({ voice: [], sms: [] });
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    loadClientTypes();
    loadProducts();
    const params = new URLSearchParams(location.search);
    const preSelectedType = params.get('type');
    const preSelectedProduct = params.get('product');
    if (preSelectedType) {
      setFormData(prev => ({ ...prev, id_client_type: preSelectedType }));
      if (preSelectedProduct) {
        setFormData(prev => ({ ...prev, product_id: preSelectedProduct }));
      }
    }
    const recaptchaScript = document.createElement('script');
    recaptchaScript.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    recaptchaScript.async = true;
    document.body.appendChild(recaptchaScript);
    return () => {
      if (document.body.contains(recaptchaScript)) {
        document.body.removeChild(recaptchaScript);
      }
    };
  }, [location]);
  useEffect(() => {
    if (formData.id_client_type && clientTypes.length > 0) {
      const selectedType = clientTypes.find(t => t.id_client_type === parseInt(formData.id_client_type));
      if (selectedType) {
        const serviceId = selectedType.service_id;
        if (serviceId === 1) {
          setFilteredProducts(products.voice || []);
        } else if (serviceId === 10) {
          setFilteredProducts(products.sms || []);
        } else {
          setFilteredProducts([]);
        }
        if (formData.product_id) {
          const productExists = (serviceId === 1 ? products.voice : products.sms || [])
            .some(p => p.id === parseInt(formData.product_id));
          if (!productExists) {
            setFormData(prev => ({ ...prev, product_id: '' }));
          }
        }
      }
    }
  }, [formData.id_client_type, clientTypes, products]);
  const loadClientTypes = async () => {
    try {
      const response = await api.get('/products/client-types');
      setClientTypes(response.data);
    } catch (error) {
      console.error('Failed to load client types:', error);
    }
  };
  const loadProducts = async () => {
    try {
      const response = await api.get('/products/all');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      if (!window.grecaptcha || !window.grecaptcha.execute) {
        throw new Error('reCAPTCHA not loaded. Please refresh the page.');
      }
      const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { 
        action: 'register' 
      });
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await api.post('/auth/register', {
        ...formData,
        recaptcha_token: recaptchaToken,
        timezone
      });
      setSuccess(response.data.message);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Registration failed';
      setError(errorMsg);
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="auth-page">
      <div className="container">
        <div className="row justify-content-center align-items-center min-vh-100">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow-lg border-0 rounded-lg">
              <div className="card-header bg-primary text-white text-center py-4">
                <h3 className="mb-0">
                  <i className="bi bi-person-plus-fill me-2"></i>
                  Create Account
                </h3>
              </div>
              <div className="card-body p-4">
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                  </div>
                )}
                {success && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    {success}
                    <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Company/Full Name *</label>
                    <input type="text" className="form-control" name="cl_name" value={formData.cl_name} onChange={handleChange} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email *</label>
                    <input type="email" className="form-control" name="contact_email" value={formData.contact_email} onChange={handleChange} required />
                  </div>
                  <div className="mb-3" style={{ position: 'absolute', left: '-9999px' }}>
                    <input
                      type="text"
                      name="website_url"
                      id="website_url"
                      value={formData.email_honeypot}
                      onChange={(e) => setFormData({ ...formData, email_honeypot: e.target.value })}
                      tabIndex="-1"
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ 
                        position: 'absolute',
                        left: '-9999px',
                        width: '1px',
                        height: '1px',
                        opacity: 0
                      }}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Password *</label>
                      <input type="password" className="form-control" name="password" value={formData.password} onChange={handleChange} required minLength={6} />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Confirm Password *</label>
                      <input type="password" className="form-control" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required minLength={6} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Client Type *</label>
                      <select className="form-select" name="id_client_type" value={formData.id_client_type} onChange={handleChange} required>
                        <option value="">Select Type</option>
                        {clientTypes.map(type => (
                          <option key={type.id_client_type} value={type.id_client_type}>
                            {type.client_type_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Product (optional)</label>
                      <select className="form-select" name="product_id" value={formData.product_id} onChange={handleChange} disabled={!formData.id_client_type}>
                        <option value="">Select Product</option>
                        {filteredProducts.map(product => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="d-grid gap-2">
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Registering...' : 'Register'}
                    </button>
                    <button type="button" className="btn btn-link" onClick={() => navigate('/login')}>
                      Already have an account? Login
                    </button>
                  </div>
                  <div className="mt-3 text-muted small text-center">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Register;